import { PropsWithChildren } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, Drawer, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ErrorBoundary } from 'common/hocs/ErrorBoundary';

import { EditDrawerProps } from './EditDrawer.types';
import { closeDrawer, selectCurrentOperation, selectOpen, selectSaveButtonEnabled, setCurrentEntity } from './editDrawerSlice';

export const DEFAULT_SUCCESS_MESSAGE = 'Changes saved!';

const EditDrawer = ({ title, saveButtonFunction, children, processing }: PropsWithChildren<EditDrawerProps>) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const open = useAppSelector(selectOpen);
    const saveButtonEnabled = useAppSelector(selectSaveButtonEnabled);
    const currentOperation = useAppSelector(selectCurrentOperation);

    const dispatch = useAppDispatch();
    const onClose = () => {
        dispatch(setCurrentEntity());
        dispatch(closeDrawer());
    };

    return (
        <Box>
            <Drawer
                anchor='right'
                open={open}
                onClose={onClose}
                PaperProps={{ sx: { width: '31%', backgroundColor: isDark ? 'background.default' : 'info.main' } }}
            >
                <ErrorBoundary>
                    <Box sx={{ pt: 4, pl: 4, pr: 4 }}>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ borderBottom: 0 }} align="left">
                                        <Typography variant="h5" gutterBottom component="div">
                                            {`${currentOperation} ${title}`}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: 0 }} align="right">
                                        <Button
                                            aria-label="Close"
                                            disabled={processing}
                                            onClick={onClose}
                                        >
                                            <CloseIcon />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                        <Box
                            sx={{
                                borderBottom: 0,
                                alignContent: 'center',
                                pl: 2,
                                pr: 3,
                                pb: 2,
                            }}
                        >
                            { children }
                        </Box>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell align="right" sx={{ borderBottom: 0 }}>
                                        <Button
                                            variant="contained"
                                            disabled={!saveButtonEnabled || processing}
                                            onClick={saveButtonFunction}
                                        >
                                            Save
                                            {processing &&
                                                <CircularProgress size='1rem' sx={{ marginLeft: '7px' }}/>}
                                        </Button>
                                    </TableCell>
                                    <TableCell align="left" sx={{ borderBottom: 0 }}>
                                        <Button
                                            variant="outlined"
                                            disabled={processing}
                                            onClick={onClose}
                                        >
                                            Cancel
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Box>
                </ErrorBoundary>
            </Drawer>
        </Box>
    );
};

export { EditDrawer };
